import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { omit, pick } from "lodash";
import { Trash2 } from "lucide-react";
import { Input } from "../../../../../../components/ui/input";
import Button from "../../../../../../components/shared/Button";
import BaseForm, { Field } from "../../../../../../components/shared/Form";
import Dialog from "../../../../../../components/shared/Dialog";
import DateInput from "../../../../../../components/shared/inputs/DateInput";
import useApi from "../../../../../../utils/api";
import { withLoadingToast } from "../../../helpers/withLoadingToast";

function createTemplateSchema({ t }: { t: (key: string) => string }) {
    return z.object({
        name: z.string().min(1, { message: t("required-name") }),
        description: z.string().optional(),
    });
}

type TemplateFormValues = z.infer<ReturnType<typeof createTemplateSchema>>;

function AddTemplate({ visible, close, values, refetch }: any) {
    const { t } = useTranslation();
    const { authPost, authPut, authDelete } = useApi();

    const title = useMemo(() => {
        if (values?.mode === "edit") {
            return t("edit-template");
        } else if (values?.mode === "clone") {
            return t("copy-template");
        } else if (values?.mode === "delete") {
            return null;
        } else {
            return t("create-template");
        }
    }, [values?.mode, t]);

    const schema = useMemo(() => createTemplateSchema({ t }), [t]);

    const form = useForm<TemplateFormValues>({
        defaultValues: {
            name: values?.mode === "edit" ? values?.name : undefined,
            description: ["edit", "clone"].includes(values?.mode) ? values?.description : undefined,
        },
        resolver: zodResolver(schema)
    });

    useEffect(() => {
        if (!visible) return;
        form.reset({
            name: values?.mode === "edit" ? values?.name : undefined,
            description: ["edit", "clone"].includes(values?.mode) ? values?.description : undefined,
        });
    }, [visible, values, form]);

    const submitMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: async (values: any) => {
            const call = values.mode === "edit" ? authPut : authPost;
            const url = values.mode === "edit"
                ? `/schedule-templates/${values.id}`
                : values?.mode === "clone"
                    ? "/schedule-templates/clone"
                    : "/schedule-templates";
            const response = await call(url, { data: omit(values, ["mode"]) });
            return { ...response, mode: values.mode };
        },
        onSuccess: (value) => {
            if (value?.runOnBackground) {
                toast.info(value.message);
            } else if (value.mode === "edit") {
                toast.success(t("template-updated-successfully"));
            } else if (values?.mode === "clone") {
                toast.success(t("template-copied-successfully"));
            } else {
                toast.success(t("template-created-successfully"))
            }
            refetch();
            onClose();
        },
        onError: (_error, variables) => {
            if (variables.mode === "edit") {
                toast.error(t("template-update-failed"));
            } else if (variables.mode === "clone") {
                toast.error(t("template-copy-failed"));
            } else {
                toast.error(t("template-create-failed"));
            }
        }
    });

    const deleteMutation = useMutation({
        ...withLoadingToast(t("deleting")),
        mutationFn: (values: any) => authDelete(`/schedule-templates/${values.id}`),
        onSuccess: () => {
            toast.success(t("template-deleted-successfully"));
            refetch();
            onClose();
        },
        onError: () => {
            toast.error(t("template-delete-failed"));
        }
    });

    const onFinish = useCallback((value: TemplateFormValues) => {
        const data: any = {
            ...pick(value, ["name", "description"]),
            mode: values?.mode
        };
        if (values?.mode === "edit") {
            data.id = values?.id;
        }
        if (values?.mode === "clone") {
            data.sourceTemplateId = values?.id;
        }
        submitMutation.mutate(data);
    }, [values, submitMutation]);

    const onClose = useCallback(() => {
        form.reset();
        close();
    }, [close, form]);

    return (
        <Dialog title={title} open={visible} close={onClose}>
            {values?.mode === "delete" ? (
                <div>
                    <div className="tw-flex tw-flex-col tw-items-center tw-mb-3 tw-p-5">
                        <Trash2 className="tw-mb-3 tw-text-red-500" size={35} />
                        <span className="tw-text-red-500 tw-font-medium">
                            {t("do-you-want-to-delete-item", { name: t("template") })}
                        </span>
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-2 tw-justify-end">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            disabled={deleteMutation.isPending}
                            loading={deleteMutation.isPending}
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            type="button"
                            onClick={() => deleteMutation.mutate(values)}
                            disabled={deleteMutation.isPending}
                            loading={deleteMutation.isPending}
                        >
                            {t("confirm")}
                        </Button>
                    </div>
                </div>
            ) : (
                <BaseForm form={form} onFinish={onFinish}>
                    <Field name="name" label={t("name")} required>
                        <Input placeholder={t("name")} />
                    </Field>

                    <Field name="description" label={t("description")}>
                        <Input placeholder={t("description")} />
                    </Field>
                    {/**if not start and end => start(currentDay) - end(undefined) */}
                    {/* <Field name="period" label={t("period")}>
                        <DateInput mode="range" />
                    </Field> */}

                    <div className="tw-flex tw-justify-end tw-gap-1">
                        <Button
                            variant="ghost"
                            type="button"
                            onClick={onClose}
                            disabled={submitMutation.isPending}
                            loading={submitMutation.isPending}
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            disabled={submitMutation.isPending}
                            loading={submitMutation.isPending}
                        >
                            {t("save")}
                        </Button>
                    </div>
                </BaseForm>
            )}
        </Dialog>
    );
}

export default AddTemplate;
